import React, { useState, useEffect } from 'react';
import { Container, Modal, Tab, Tabs, Button, Table, Form, Alert, Spinner } from 'react-bootstrap';
import { API_URL } from '../../utils/constants';
import './SetupGroupsScreen.css';

function SetupGroupsScreen() {
  const [showModal, setShowModal] = useState(true);
  const [activeTab, setActiveTab] = useState('configuration');

  // Group Type state
  const [groupTypes, setGroupTypes] = useState([]);
  const [loadingGroupTypes, setLoadingGroupTypes] = useState(true);
  const [savingGroupType, setSavingGroupType] = useState(false);
  
  // Group Type Modal state
  const [showGroupTypeModal, setShowGroupTypeModal] = useState(false);
  const [groupTypeModalMode, setGroupTypeModalMode] = useState('add'); // 'add' or 'edit'
  const [selectedGroupType, setSelectedGroupType] = useState(null);
  const [groupTypeForm, setGroupTypeForm] = useState({ name: '', active: true, sort_order: 0 });
  const [groupTypeErrors, setGroupTypeErrors] = useState({});

  // Error and success messages
  const [message, setMessage] = useState({ text: '', variant: '', show: false });

  // Fetch data on component mount
  useEffect(() => {
    fetchGroupTypes();
  }, []);

  // API Functions for Group Types
  const fetchGroupTypes = async () => {
    try {
      setLoadingGroupTypes(true);
      const response = await fetch(`${API_URL}/group-types`);
      if (!response.ok) throw new Error('Failed to fetch group types');
      const data = await response.json();
      setGroupTypes(data);
    } catch (error) {
      console.error('Error fetching group types:', error);
      setMessage({ text: 'Failed to load group types', variant: 'danger', show: true });
    } finally {
      setLoadingGroupTypes(false);
    }
  };

  const updateGroupType = async (id, updates) => {
    try {
      const response = await fetch(`${API_URL}/group-types/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update group type');
      }
      
      const updatedType = await response.json();
      setGroupTypes(groupTypes.map(gt => gt.id === id ? updatedType : gt));
      setMessage({ text: 'Group type updated successfully', variant: 'success', show: true });
    } catch (error) {
      console.error('Error updating group type:', error);
      setMessage({ text: error.message, variant: 'danger', show: true });
    }
  };

  const deleteGroupType = async (id) => {
    if (!window.confirm('Are you sure you want to delete this group type?')) return;
    
    try {
      const response = await fetch(`${API_URL}/group-types/${id}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete group type');
      }
      
      setGroupTypes(groupTypes.filter(gt => gt.id !== id));
      setMessage({ text: 'Group type deleted successfully', variant: 'success', show: true });
    } catch (error) {
      console.error('Error deleting group type:', error);
      setMessage({ text: error.message, variant: 'danger', show: true });
    }
  };

  // Group Type Modal handlers
  const handleAddGroupType = () => {
    setGroupTypeModalMode('add');
    setSelectedGroupType(null);
    setGroupTypeForm({ name: '', active: true, sort_order: 0 });
    setGroupTypeErrors({});
    setShowGroupTypeModal(true);
  };

  const handleEditGroupType = (groupType) => {
    setGroupTypeModalMode('edit');
    setSelectedGroupType(groupType);
    setGroupTypeForm({ name: groupType.name, active: groupType.active, sort_order: groupType.sort_order || 0 });
    setGroupTypeErrors({});
    setShowGroupTypeModal(true);
  };

  const handleGroupTypeInputChange = (field, value) => {
    setGroupTypeForm(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (groupTypeErrors[field]) {
      setGroupTypeErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateGroupTypeForm = () => {
    const errors = {};
    if (!groupTypeForm.name.trim()) {
      errors.name = 'Name is required';
    }
    return errors;
  };

  const handleSaveGroupType = async () => {
    const errors = validateGroupTypeForm();
    if (Object.keys(errors).length > 0) {
      setGroupTypeErrors(errors);
      return;
    }

    try {
      setSavingGroupType(true);
      if (groupTypeModalMode === 'add') {
        const response = await fetch(`${API_URL}/group-types`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(groupTypeForm)
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to create group type');
        }
        
        const newType = await response.json();
        setGroupTypes([...groupTypes, newType]);
        setMessage({ text: 'Group type created successfully', variant: 'success', show: true });
      } else {
        await updateGroupType(selectedGroupType.id, groupTypeForm);
      }
      
      setShowGroupTypeModal(false);
      setGroupTypeForm({ name: '', active: true, sort_order: 0 });
    } catch (error) {
      setMessage({ text: error.message, variant: 'danger', show: true });
    } finally {
      setSavingGroupType(false);
    }
  };

  const handleCloseGroupTypeModal = () => {
    setShowGroupTypeModal(false);
    setGroupTypeForm({ name: '', active: true, sort_order: 0 });
    setGroupTypeErrors({});
  };

  const handleCloseModal = () => {
    // Modal can only be closed by clicking the X button or specific buttons
    // This prevents accidental closing
  };

  const handleModalClose = () => {
    setShowModal(false);
    setActiveTab('configuration');
  };

  const handleSaveAndClose = () => {
    // Save any changes and close modal
    setShowModal(false);
    setActiveTab('configuration');
  };

  const handleSave = () => {
    // Save any changes but keep modal open
    console.log('Saving changes...');
  };

  // Auto-hide messages after 5 seconds
  useEffect(() => {
    if (message.show) {
      const timer = setTimeout(() => {
        setMessage({ text: '', variant: '', show: false });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message.show]);

  return (
    <Container fluid className="setup-groups-screen">
      <div className="screen-header">
        <h1>Setup - Groups</h1>
        <p>Configure groups settings and management</p>
      </div>

      <div className="screen-content">
        {/* Message Alert */}
        {message.show && (
          <Alert 
            variant={message.variant} 
            dismissible 
            onClose={() => setMessage({ text: '', variant: '', show: false })}
            className="mb-3"
          >
            {message.text}
          </Alert>
        )}

        {/* Main Setup Modal */}
        <Modal 
          show={showModal} 
          onHide={handleCloseModal}
          size="xl"
          backdrop="static"
          keyboard={false}
          className="setup-modal"
        >
          <Modal.Header>
            <Modal.Title>Setup - Groups</Modal.Title>
            <Button
              variant="link"
              onClick={handleModalClose}
              className="close-button"
            >
              ×
            </Button>
          </Modal.Header>
          
          <Modal.Body>
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="setup-tabs"
            >
              <Tab eventKey="configuration" title="Configuration">
                <div className="tab-content">
                  <div className="tab-header">
                    <h4>Configuration</h4>
                    <p>General group configuration settings will be added here.</p>
                  </div>
                </div>
              </Tab>

              <Tab eventKey="group-type" title="Group Type">
                <div className="tab-content">
                  <div className="tab-header">
                    <h4>Group Types</h4>
                    <Button 
                      variant="success" 
                      onClick={handleAddGroupType}
                      size="sm"
                    >
                      ➕ Add Group Type
                    </Button>
                  </div>
                  
                  {loadingGroupTypes ? (
                    <div className="text-center py-4">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table striped bordered hover>
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Sort Order</th>
                            <th>Active</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groupTypes.map((gt) => (
                            <tr key={gt.id}>
                              <td>{gt.name}</td>
                              <td>{gt.sort_order || 0}</td>
                              <td>
                                <span className={`badge ${gt.active ? 'bg-success' : 'bg-secondary'}`}>
                                  {gt.active ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleEditGroupType(gt)}
                                  className="me-2"
                                >
                                  ✏️ Edit
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => deleteGroupType(gt.id)}
                                >
                                  🗑️ Delete
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>
              </Tab>
            </Tabs>
          </Modal.Body>
          
          <Modal.Footer>
            <Button onClick={handleSaveAndClose}>
              Save & Close
            </Button>
            <Button onClick={handleSave}>
              Save
            </Button>
            <Button onClick={handleModalClose}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Group Type Modal */}
        <Modal 
          show={showGroupTypeModal} 
          onHide={handleCloseGroupTypeModal}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {groupTypeModalMode === 'add' ? 'Add Group Type' : 'Edit Group Type'}
            </Modal.Title>
          </Modal.Header>
          
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={groupTypeForm.name}
                  onChange={(e) => handleGroupTypeInputChange('name', e.target.value)}
                  isInvalid={!!groupTypeErrors.name}
                  placeholder="e.g., Staff, Guests, Management"
                />
                <Form.Control.Feedback type="invalid">
                  {groupTypeErrors.name}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Sort Order</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={groupTypeForm.sort_order}
                  onChange={(e) => handleGroupTypeInputChange('sort_order', parseInt(e.target.value) || 0)}
                  placeholder="0"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Active"
                  checked={groupTypeForm.active}
                  onChange={(e) => handleGroupTypeInputChange('active', e.target.checked)}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseGroupTypeModal}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSaveGroupType}
              disabled={savingGroupType}
            >
              {savingGroupType ? 'Saving...' : 'Save'}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </Container>
  );
}

export default SetupGroupsScreen;
